import React, { useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { useTheme, Box, Typography,
    Table, TableBody, TableCell,
    TableContainer, TableHead,
    TableRow, Paper, Collapse, 
} from "@mui/material";
import { LAYOUT_HEIGHT } from "state/constants";


const OtpTableau = ({ data }) => {
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const primaryDark = theme.palette.primary.dark;
    const [openCell, setOpenCell] = useState(null);
    const [blinkCell, setBlinkCell] = useState(null);

    const handleClick = (cellKey) => {
        setOpenCell(openCell === cellKey ? null : cellKey);
        setBlinkCell(cellKey);
        setTimeout(() => setBlinkCell(null), 600);
    };

    if (!data || data.length === 0) {
        return (
            <Box p={1} sx={{ height: '100%', width: '100%' }}>        
                <Typography>Nothing to display</Typography>
            </Box>
        );
    }

    const getBgColor = (otp) => {
        if (otp === null) return "transparent";
        if (otp < 60) return "#f2292d";
        if (otp < 70) return "#f29129";
        if (otp < 80) return "#d7f229";
        if (otp >= 95) return primaryDark;
        return null;
    };

    const parsed = data.map((d) => {
        const [field1, field2] = d.key.split("|");
        return {
            field1,
            field2,
            otp: d.otp,
            delayedFlights: d.delayedFlights || 0,
            totalFlights: d.totalFlights || 0,
            flights: d.flights || []
        };
    });

    const isValidDateString = (v) =>
        typeof v === 'string' &&
        isNaN(v) && 
        !isNaN(Date.parse(v));

    const field1Values = [...new Set(parsed.map((p) => p.field1))].sort((a, b) => {
        if (!isNaN(Date.parse(a)) && !isNaN(Date.parse(b))) {
            return new Date(a) - new Date(b);
        }
        return a.localeCompare(b);
    });

    const field2Values = [...new Set(parsed.map((p) => p.field2))].sort();

    return (
        <TableContainer component={Paper} sx={{ maxHeight: LAYOUT_HEIGHT }}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: "bold", minWidth: "90px" }}>
                            Field
                        </TableCell>
                        {field1Values.map((field1) => (
                            <TableCell key={field1} align="center" sx={{ fontWeight: "bold" }}>
                                {isValidDateString(field1)
                                    ? format(new Date(field1), "dd.MM.yy")
                                    : field1
                                }
                            </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            Average
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {field2Values.map((field2) => {
                        const rowValues = field1Values.map((field1) =>
                            parsed.find((p) => p.field1 === field1 && p.field2 === field2)
                        );
                        const totalDelayed = rowValues.reduce((s, v) => s + (v?.delayedFlights || 0), 0);
                        const totalFlights = rowValues.reduce((s, v) => s + (v?.totalFlights || 0), 0);
                        const rowOtp = totalFlights > 0
                            ? ((1 - totalDelayed / totalFlights) * 100).toFixed(1)
                            : null;

                        return (
                            <React.Fragment key={field2}>
                                <TableRow>
                                    <TableCell component="th" scope="row">
                                        {field2 || ""}
                                    </TableCell>
                                        {field1Values.map((field1) => {
                                            const found = parsed.find(
                                            (p) => p.field1 === field1 && p.field2 === field2
                                            );
                                            const cellKey = `${field1}-${field2}`;
                                            return (
                                            <TableCell
                                                key={cellKey}
                                                align="center"
                                                onClick={() => handleClick(cellKey)}
                                                sx={{
                                                cursor: "pointer",
                                                color: getBgColor(found ? found.otp : null),
                                                backgroundColor:
                                                    openCell === cellKey
                                                    ? theme.palette.action.selected
                                                    : "inherit",
                                                fontWeight: openCell === cellKey ? "bold" : "normal",
                                                ...(blinkCell === cellKey && {
                                                    animation: "blink 0.6s ease-in-out"
                                                }),
                                                "@keyframes blink": {
                                                    "0%": {
                                                    backgroundColor: theme.palette.primary.light,
                                                    color: "#fff"
                                                    },
                                                    "50%": {
                                                    backgroundColor: theme.palette.action.hover
                                                    },
                                                    "100%": { backgroundColor: "inherit" }
                                                }
                                                }}
                                            >
                                                {found ? `${found.otp}%` : "-"}
                                            </TableCell>
                                            );
                                        })}
                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontWeight: "bold",
                                            color: getBgColor(rowOtp ? parseFloat(rowOtp) : null)
                                        }}
                                    >
                                        {rowOtp ? `${rowOtp}%` : "-"}
                                    </TableCell>
                                </TableRow>

                                {/* Dropdown row */}
                                {field1Values.map((field1) => {
                                    const found = parsed.find(
                                        (p) => p.field1 === field1 && p.field2 === field2
                                    );
                                    const cellKey = `${field1}-${field2}`;
                                    return (
                                        openCell === cellKey &&
                                        found && found.flights.length > 0 && (
                                            <TableRow key={`${cellKey}-details`}>
                                                <TableCell colSpan={field1Values.length + 2} sx={{ p: 0 }}>
                                                    <Collapse
                                                        in={openCell === cellKey}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        <Box marginLeft={2}>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow
                                                                        sx={{ "& .MuiTableCell-root": { color: primaryDark } }}
                                                                    >
                                                                        <TableCell>Date</TableCell>
                                                                        <TableCell>FlightNo</TableCell>
                                                                        <TableCell>Sector</TableCell>
                                                                        <TableCell>Std</TableCell>
                                                                        <TableCell>Sta</TableCell>
                                                                        <TableCell>Atd</TableCell>
                                                                        <TableCell>Ata</TableCell>
                                                                        <TableCell>OTP</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {found.flights.map((f, idx) => {
                                                                        const flightDate =
                                                                        timeMode === "UTC"
                                                                            ? f.flightDate
                                                                            : f.flightDateLocalBase;
                                                                        const std = timeMode === "UTC" ? f.std : f.stdLocalBase;
                                                                        const sta = timeMode === "UTC" ? f.sta : f.staLocalBase;
                                                                        const atd = timeMode === "UTC" ? f.atd : f.atdLocalBase;
                                                                        const ata = timeMode === "UTC" ? f.ata : f.ataLocalBase;

                                                                        return (
                                                                            <TableRow key={idx}>
                                                                                <TableCell>
                                                                                    {format(new Date(flightDate), "dd.MM.yy")}
                                                                                </TableCell>
                                                                                <TableCell>{f.flightNo}</TableCell>
                                                                                <TableCell>{f.sector}</TableCell>
                                                                                <TableCell>{std}</TableCell>
                                                                                <TableCell>{sta}</TableCell>
                                                                                <TableCell>{atd}</TableCell>
                                                                                <TableCell>{ata}</TableCell>
                                                                                <TableCell
                                                                                    sx={{
                                                                                        color: (f.otp || "").toLowerCase() === "delayed" 
                                                                                            ? "#f2292d" 
                                                                                            : "inherit",
                                                                                    }}
                                                                                >
                                                                                    {f.otp}
                                                                                </TableCell>

                                                                            </TableRow>
                                                                        );
                                                                    })}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
                {/* --- Overall Row --- */}
                {(() => {
                    const totalDelayedAll = parsed.reduce((s, v) => s + (v.delayedFlights || 0), 0);
                    const totalFlightsAll = parsed.reduce((s, v) => s + (v.totalFlights || 0), 0);
                    const overallOtp = totalFlightsAll > 0
                        ? ((1 - totalDelayedAll / totalFlightsAll) * 100).toFixed(1)
                        : null;

                    return (
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>Average</TableCell>
                            {field1Values.map((field1) => {
                                const colValues = parsed.filter((p) => p.field1 === field1);
                                const totalDelayed = colValues.reduce((s, v) => s + (v?.delayedFlights || 0), 0);
                                const totalFlights = colValues.reduce((s, v) => s + (v?.totalFlights || 0), 0);
                                const colOtp = totalFlights > 0
                                    ? ((1 - totalDelayed / totalFlights) * 100).toFixed(1)
                                    : null;

                                return (
                                    <TableCell
                                        key={`overall-${field1}`}
                                        align="center"
                                        sx={{
                                            fontWeight: "bold",
                                            color: getBgColor(colOtp ? parseFloat(colOtp) : null),
                                        }}
                                    >
                                        {colOtp ? `${colOtp}%` : "-"}
                                    </TableCell>
                                );
                            })}
                            <TableCell
                                align="center"
                                sx={{
                                    fontWeight: "bold",
                                    color: getBgColor(overallOtp ? parseFloat(overallOtp) : null),
                                }}
                            >
                                {overallOtp ? `${overallOtp}%` : "-"}
                            </TableCell>
                        </TableRow>
                    );
                })()}
            </Table>
        </TableContainer>
    );
};

export default OtpTableau;
